import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, useAuth } from '../App';
import { useTheme/*, THEMES*/ } from '../theme';
import FancyDropdown from '../components/FancyDropdown';
import FestiveEffects, { getTileDecoration, SantaHat, SnowAccumulation } from '../components/FestiveEffects';
import { apiUrl, fileUrl, imageUrl } from '../api';

function formatNumberSpaces(num) {
  if (num === undefined || num === null) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// Mobile marquee extracted and memoized to avoid animation reset on parent re-renders
const MobileMarquee = memo(function MobileMarquee() {
  const { tokens } = useTheme();
  const [paused, setPaused] = React.useState(false);
  const wrapperStyle = {
    width: 'min(480px, 96vw)',
    margin: '-30px auto 0px auto', // keep current positioning
  };
  const railStyle = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 14,
    background: tokens.panel,
    border: `1px solid ${tokens.border}`,
    boxShadow: `0 8px 22px ${tokens.indicatorGlow}, inset 0 1px 0 ${tokens.border}`,
    padding: '8px 0',
  };
  const rowStyle = {
    display: 'inline-flex',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    gap: 24,
    paddingRight: 24,
  color: tokens.text,
    fontWeight: 900,
    fontSize: 13,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    textShadow: `0 1px 4px ${tokens.indicatorGlow}`,
  };
  const items = [
    'BUY‑BACK DIRECT PE SITE',
    'ACHIZIȚII AUTO',
    'GĂSEȘTE MAȘINA PENTRU TINE',
    'FINANȚARE RAPIDĂ',
    'EVALUARE RAPIDĂ ONLINE',
  ];

  return (
    <div style={wrapperStyle}>
      <style>{`
        @keyframes marqueeScrollFast {
          0% { transform: translate3d(0,0,0); }
          100% { transform: translate3d(-50%,0,0); }
        }
      `}</style>
      <div
        style={railStyle}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        onTouchCancel={() => setPaused(false)}
      >
        <div
          style={{
            display: 'flex',
            width: 'max-content',
            animation: 'marqueeScrollFast 7.5s linear infinite',
            animationPlayState: paused ? 'paused' : 'running',
            willChange: 'transform',
          }}
        >
          <div style={rowStyle}>
            {items.map((txt, i) => (
              <React.Fragment key={`a-${i}`}>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{txt}</span>
                <span style={{ color: tokens.accent, opacity: 0.9 }}>•</span>
              </React.Fragment>
            ))}
          </div>
          <div style={rowStyle} aria-hidden="true">
            {items.map((txt, i) => (
              <React.Fragment key={`b-${i}`}>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{txt}</span>
                <span style={{ color: tokens.accent, opacity: 0.9 }}>•</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default function Listings() {
  const { tokens, themeKey, setThemeKey } = useTheme();
  const isLightTheme = ['redLight', 'blueLight', 'tealGray'].includes(themeKey);
  const { token } = useAuth();
  const [cars, setCars] = useState([]);
  const [initialized, setInitialized] = useState(false);
  // Site info state (footer + mobile buttons). Stored locally for now.
  const [siteInfo, setSiteInfo] = useState(() => { try { return JSON.parse(localStorage.getItem('siteInfo') || '{}'); } catch { return {}; } });
  const [tempSiteInfo, setTempSiteInfo] = useState({});
  const [contactOpen, setContactOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [bgImageUrl, setBgImageUrl] = useState(() => {
    try { return localStorage.getItem('heroBackgroundImage') || ''; } catch { return ''; }
  });
  const [tempBgUrl, setTempBgUrl] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [squareTiles, setSquareTiles] = useState(() => {
    try { return localStorage.getItem('homeSquareTiles') === '1'; } catch { return false; }
  });
  const [tempSquareTiles, setTempSquareTiles] = useState(false);
  const [festiveTheme, setFestiveTheme] = useState(() => {
    try { return localStorage.getItem('homeFestiveTheme') || 'none'; } catch { return 'none'; }
  });
  const [tempFestiveTheme, setTempFestiveTheme] = useState('none');
  const [hideFeatured, setHideFeatured] = useState(() => {
    try { return localStorage.getItem('homeHideFeatured') === '1'; } catch { return false; }
  });
  const [tempHideFeatured, setTempHideFeatured] = useState(false);
  const [filters, setFilters] = useState({
    brand: '',
    model: '',
    yearFrom: '',
    yearTo: '',
    kmFrom: '',
    kmTo: '',
    priceFrom: '',
    priceTo: '',
    fuel: '',
    transmission: '',
    body: '',
  });
  // Applied filters used for results; results update only after clicking "Vezi anunțurile"
  const [appliedFilters, setAppliedFilters] = useState({
    brand: '',
    model: '',
    yearFrom: '',
    yearTo: '',
    kmFrom: '',
    kmTo: '',
    priceFrom: '',
    priceTo: '',
    fuel: '',
    transmission: '',
    body: '',
  });

  const { language } = useLanguage();
  const navigate = useNavigate();
  const resultsRef = useRef(null);

  // Fetch cars on mount and whenever user navigates back to this page
  useEffect(() => {
    const fetchCars = () => {
      fetch(apiUrl('/api/cars'))
        .then(res => res.json())
        .then(data => { setCars(data); setInitialized(true); })
        .catch(() => { setCars([]); setInitialized(true); });
    };
    fetchCars();
    // Refetch when page becomes visible (user switches back to tab or navigates back)
    const handleVisibility = () => {
      if (!document.hidden) fetchCars();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Also refetch when window gains focus (covers navigate-back case)
    window.addEventListener('focus', fetchCars);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', fetchCars);
    };
  }, []);

  // Fetch settings from server on mount
  useEffect(() => {
    const applySettings = (data) => {
      // Do not override temporary edits while modal is open
      if (showEditModal) return;
      if (data.siteInfo && JSON.stringify(data.siteInfo) !== JSON.stringify(siteInfo)) {
        setSiteInfo(data.siteInfo);
        try { localStorage.setItem('siteInfo', JSON.stringify(data.siteInfo)); } catch {}
      }
      if (data.festiveTheme && data.festiveTheme !== festiveTheme) {
        setFestiveTheme(data.festiveTheme);
        try { localStorage.setItem('homeFestiveTheme', data.festiveTheme); } catch {}
      }
      if (data.heroBackgroundImage !== undefined && data.heroBackgroundImage !== bgImageUrl) {
        setBgImageUrl(data.heroBackgroundImage);
        try { localStorage.setItem('heroBackgroundImage', data.heroBackgroundImage); } catch {}
      }
      if (data.squareTiles !== undefined && data.squareTiles !== squareTiles) {
        setSquareTiles(data.squareTiles);
        try { localStorage.setItem('homeSquareTiles', data.squareTiles ? '1' : '0'); } catch {}
      }
      if (data.hideFeatured !== undefined && data.hideFeatured !== hideFeatured) {
        setHideFeatured(data.hideFeatured);
        try { localStorage.setItem('homeHideFeatured', data.hideFeatured ? '1' : '0'); } catch {}
      }
    };
    const fetchSettings = () => {
      fetch(apiUrl('/api/settings'))
        .then(res => res.json())
        .then(applySettings)
        .catch(err => console.error('Failed to fetch settings:', err));
    };
    fetchSettings();
    const id = setInterval(fetchSettings, 20000); // 20s polling for cross-browser sync
    return () => clearInterval(id);
  }, [showEditModal, siteInfo, festiveTheme, bgImageUrl, squareTiles, hideFeatured]);

  const t = {
    en: {
      title: 'Car Listings',
      searchTitle: 'Find your dream car',
      searchDetailed: 'Detailed search',
      brand: 'Brand',
      model: 'Model',
      year: 'Year',
      yearFrom: 'From',
      yearTo: 'To',
      km: 'Mileage (km)',
      kmFrom: 'From',
      kmTo: 'To',
      price: 'Price',
      priceFrom: 'From',
      priceTo: 'To',
      fuel: 'Fuel',
      transmission: 'Transmission',
      body: 'Body',
      choose: 'Choose',
      reset: 'Reset filters',
      see: 'See listings',
      type: 'Type',
      seller: 'Seller',
      description: 'Description',
      removeBg: 'Remove Background',
      cancel: 'Cancel',
      saveAllDevices: 'Save on all devices',
      sold: 'Sold',
      reserved: 'Reserved',
      featured: 'Featured',
    },
    ro: {
      title: 'Mașinile Disponibile',
      searchTitle: 'Caută Mașina Visată',
      searchDetailed: 'Căutare detaliată',
      brand: 'Marcă',
      model: 'Model',
      year: 'An',
      yearFrom: 'De la',
      yearTo: 'Până la',
      km: 'Kilometraj (km)',
      kmFrom: 'De la',
      kmTo: 'Până la',
      price: 'Preț',
      priceFrom: 'De la',
      priceTo: 'Până la',
      fuel: 'Combustibil',
      transmission: 'Transmisie',
      body: 'Caroserie',
      choose: 'Alege',
      reset: 'Anulează filtrarea',
      see: 'Vezi anunțurile',
      type: 'Tip',
      seller: 'Vânzător',
      description: 'Descriere',
      removeBg: 'Elimină fundal',
      cancel: 'Anulează',
      saveAllDevices: 'Salvează pe toate dispozitivele',
      sold: 'Vândut',
      reserved: 'Rezervat',
      featured: 'Recomandate',
    },
  };

  const tr = t[language] || t.ro;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 760;
  const [showAdvanced, setShowAdvanced] = useState(!isMobile);
  // Baseline scale after reductions, now uniformly increased by 35%
  const baselineScale = isMobile ? 1 : 0.55;
  const boostFactor = isMobile ? 1 : 1.35; // used for width and initial size boost
  const finalScale = baselineScale * boostFactor;
  // Additional user-requested +30% increase, without changing panel width
  const extraBoost = isMobile ? 1 : 1.3;
  const sizeScale = finalScale * extraBoost;
  const sFont = Math.max(11, Math.round(15 * sizeScale));
  const sLabel = Math.max(11, Math.round(14 * sizeScale));
  const sPadV = Math.max(6, Math.round(10 * sizeScale));
  const sPadH = Math.max(8, Math.round(12 * sizeScale));
  const sGap = Math.max(12, Math.round(16 * sizeScale));
  const sGapSmall = Math.max(6, Math.round(8 * sizeScale));
  const sInnerPad = Math.max(12, Math.round(16 * sizeScale));
  const sRadiusInput = Math.max(6, Math.round(8 * sizeScale));
  const sRadiusPanelBase = Math.max(12, Math.round(16 * sizeScale));
  const effectiveSquareTiles = showEditModal ? tempSquareTiles : squareTiles;
  const effectiveFestiveTheme = showEditModal ? tempFestiveTheme : festiveTheme;
  const effectiveHideFeatured = showEditModal ? tempHideFeatured : hideFeatured;
  const tileRadius = effectiveSquareTiles ? 0 : 16;
  const imageRadius = effectiveSquareTiles ? 0 : 12;
  const featuredOuterRadius = effectiveSquareTiles ? 0 : 18;
  const featuredInnerRadius = effectiveSquareTiles ? 0 : 16;
  const sRadiusPanel = effectiveSquareTiles ? 0 : sRadiusPanelBase;
  const arrowOffset = isMobile ? 18 : Math.max(14, Math.round(18 * sizeScale));
  const arrowGap = isMobile ? 6 : Math.max(5, Math.round(6 * sizeScale));
  const arrowSize = isMobile ? 6 : Math.max(5, Math.round(6 * sizeScale));
  // Tighter mobile button styling for 'Vezi anunțurile'
  const btnPadV = isMobile ? 10 : Math.max(10, Math.round(12 * sizeScale));
  const btnPadH = isMobile ? 16 : Math.max(16, Math.round(22 * sizeScale));
  const btnFont = isMobile ? 12 : Math.max(14, Math.round(16 * sizeScale));
  const btnRadius = isMobile ? 8 : Math.max(8, Math.round(12 * sizeScale));
  const btnShadow = isMobile ? `0 4px 12px ${tokens.indicatorGlow}` : `0 6px 18px ${tokens.indicatorGlow}`;

  const getVal = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && `${v}` !== '') return v;
    }
    return undefined;
  };

  const uniqueSorted = (arr) => Array.from(new Set(arr.filter(Boolean).map(v => `${v}`.trim()))).sort((a, b) => a.localeCompare(b));

  // Robust brand derivation: prefer explicit fields, then fall back to first token of title
  const deriveBrand = (c) => {
    const explicit = getVal(c, ['marca', 'brand']);
    if (explicit && `${explicit}`.trim()) return `${explicit}`.trim();
    const title = (c?.title || '').toString().trim();
    if (title) return title.split(/\s+/)[0];
    return '';
  };

  // Manufacturers only (no model)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const brands = useMemo(() => uniqueSorted(cars.map(c => deriveBrand(c))), [cars]);

  // Models by brand and overall
  const modelsByBrand = useMemo(() => {
    const map = {};
    cars.forEach(c => {
      const b = deriveBrand(c);
      const m = (c.model || '').toString().trim();
      if (!b || !m) return;
      if (!map[b]) map[b] = new Set();
      map[b].add(m);
    });
    return map;
  }, [cars]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allModels = useMemo(() => uniqueSorted(cars.map(c => (c.model || '').toString().trim())), [cars]);

  const models = useMemo(() => {
    if (filters.brand) {
      const key = Object.keys(modelsByBrand).find(k => k.toLowerCase() === filters.brand.toLowerCase());
      if (key && modelsByBrand[key]) return uniqueSorted(Array.from(modelsByBrand[key]));
      return [];
    }
    return allModels;
  }, [filters.brand, modelsByBrand, allModels]);

  const fuels = useMemo(() => uniqueSorted(cars.map(c => getVal(c, ['fuel', 'combustibil']))), [cars]);
  const transmissions = useMemo(() => uniqueSorted(cars.map(c => getVal(c, ['gearbox', 'transmission', 'cutie']))), [cars]);
  const bodies = useMemo(() => uniqueSorted(cars.map(c => getVal(c, ['type', 'caroserie', 'body']))), [cars]);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 40 }, (_, i) => `${now - i}`);
  }, []);
  const priceOptions = useMemo(() => [
    '2000','3000','5000','7000','10000','15000','20000','30000','50000'
  ], []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'brand' ? { model: '' } : {})
    }));
  };

  const resetFilters = () => setFilters({ brand: '', model: '', yearFrom: '', yearTo: '', kmFrom: '', kmTo: '', priceFrom: '', priceTo: '', fuel: '', transmission: '', body: '' });
  // const scrollToResults = () => resultsRef.current && resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const applyAndScroll = () => { setAppliedFilters(filters); /*scrollToResults();*/ };

  const toNum = (v) => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = Number(String(v).replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : undefined;
  };

  const filtered = useMemo(() => {
    return cars.filter(car => {
  const brand = deriveBrand(car).toLowerCase();
      const modelVal = (car.model || '').toString().toLowerCase();
      const year = toNum(car.year);
      const km = toNum(getVal(car, ['km', 'kilometraj', 'mileage']));
      const price = toNum(car.price);
      const fuel = (getVal(car, ['fuel', 'combustibil']) || '').toString().toLowerCase();
      const transmission = (getVal(car, ['gearbox', 'transmission', 'cutie']) || '').toString().toLowerCase();
      const body = (getVal(car, ['type', 'caroserie', 'body']) || '').toString().toLowerCase();
      // Use appliedFilters for actual filtering
      if (appliedFilters.brand && brand !== appliedFilters.brand.toLowerCase()) return false;
      if (appliedFilters.model && modelVal !== appliedFilters.model.toLowerCase()) return false;
      if (appliedFilters.fuel && fuel !== appliedFilters.fuel.toLowerCase()) return false;
      if (appliedFilters.transmission && transmission !== appliedFilters.transmission.toLowerCase()) return false;
      if (appliedFilters.body && body !== appliedFilters.body.toLowerCase()) return false;

      const yFrom = toNum(appliedFilters.yearFrom), yTo = toNum(appliedFilters.yearTo);
      if (yFrom !== undefined && (year === undefined || year < yFrom)) return false;
      if (yTo !== undefined && (year === undefined || year > yTo)) return false;

      const kFrom = toNum(appliedFilters.kmFrom), kTo = toNum(appliedFilters.kmTo);
      if (kFrom !== undefined && (km === undefined || km < kFrom)) return false;
      if (kTo !== undefined && (km === undefined || km > kTo)) return false;

      const pFrom = toNum(appliedFilters.priceFrom), pTo = toNum(appliedFilters.priceTo);
      if (pFrom !== undefined && (price === undefined || price < pFrom)) return false;
      if (pTo !== undefined && (price === undefined || price > pTo)) return false;

      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, appliedFilters]);

  // Styles
  const fieldLabel = { fontWeight: 700, color: tokens.text, marginBottom: Math.max(4, Math.round(6 * sizeScale)), fontSize: sLabel };
  const inputBase = {
    background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: sRadiusInput,
    padding: `${sPadV}px ${sPadH}px`, fontSize: sFont, outline: 'none', width: '100%'
  };
  const selectStyle = {
    ...inputBase,
    appearance: 'none',
    backgroundImage: `linear-gradient(45deg, transparent 50%, ${tokens.accent} 50%), linear-gradient(135deg, ${tokens.accent} 50%, transparent 50%)`,
    backgroundPosition: `calc(100% - ${arrowOffset}px) calc(1em + 2px), calc(100% - ${Math.max(6, arrowOffset - arrowGap)}px) calc(1em + 2px)`,
    backgroundSize: `${arrowSize}px ${arrowSize}px, ${arrowSize}px ${arrowSize}px`,
    backgroundRepeat: 'no-repeat'
  };
  const grid = {
    display: 'grid',
    gridTemplateColumns: window.innerWidth < 760 ? '1fr' : '1fr 1fr',
    gap: sGap,
  };
  const compactGrid = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: sGap,
    alignItems: 'end'
  };

  // Featured slideshow - random unique cars (always different) independent of filters
  const [featuredIdx, setFeaturedIdx] = useState(0);
  const sampleUnique = (arr, n) => {
    const copy = Array.isArray(arr) ? arr.slice() : [];
    // Shuffle
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    const seen = new Set();
    const out = [];
    for (const it of copy) {
      const id = it && it._id ? it._id : undefined;
      if (id && !seen.has(id)) {
        seen.add(id);
        out.push(it);
        if (out.length >= n) break;
      }
    }
    return out;
  };
  const featured = useMemo(() => {
    const available = cars.filter(c => c.status !== 'sold' && c.status !== 'reserved' && !c.sold);
    return sampleUnique(available, Math.min(6, available.length || 0));
  }, [cars]);
  // Reset index if featured set changes size
  useEffect(() => { setFeaturedIdx(0); }, [featured.length]);
  useEffect(() => {
    if (!featured || featured.length <= 1) return;
    const id = setInterval(() => setFeaturedIdx(i => (i + 1) % featured.length), 5000);
    return () => clearInterval(id);
  }, [featured.length]);
  const next = () => setFeaturedIdx(i => (i + 1) % (featured.length || 1));
  const prev = () => setFeaturedIdx(i => (i - 1 + (featured.length || 1)) % (featured.length || 1));
  
  // Touch/swipe handling for featured carousel
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) next();
    if (isRightSwipe) prev();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    setUploadingFile(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const base64 = evt.target?.result;
      if (typeof base64 === 'string') {
        setTempBgUrl(base64);
      }
      setUploadingFile(false);
    };
    reader.onerror = () => {
      alert('Failed to read file');
      setUploadingFile(false);
    };
    reader.readAsDataURL(file);
  };

  const handleApplyChanges = async () => {
    try {
      // Save to localStorage first
      localStorage.setItem('heroBackgroundImage', tempBgUrl);
      localStorage.setItem('homeSquareTiles', tempSquareTiles ? '1' : '0');
      localStorage.setItem('homeFestiveTheme', tempFestiveTheme);
      localStorage.setItem('homeHideFeatured', tempHideFeatured ? '1' : '0');
      
      // Update state immediately
      setBgImageUrl(tempBgUrl);
      setSquareTiles(tempSquareTiles);
      setFestiveTheme(tempFestiveTheme);
      setHideFeatured(tempHideFeatured);
      
      // Save to server for persistence across devices
      if (token) {
        const response = await fetch(apiUrl('/api/settings'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({
            festiveTheme: tempFestiveTheme,
            heroBackgroundImage: tempBgUrl,
            squareTiles: tempSquareTiles,
            hideFeatured: tempHideFeatured
          })
        });
        
        if (!response.ok) {
          console.error('Server rejected settings save:', response.status);
          alert('⚠ Modificările au fost salvate local, dar nu s-au putut sincroniza pe server.');
        }
      }
      
      setShowEditModal(false);
    } catch (e) {
      console.error('Failed to save changes:', e);
      alert('Failed to save changes');
    }
  };

  const handleCancelEdit = () => {
    // Revert to saved values
    setTempBgUrl(bgImageUrl);
    setTempSquareTiles(squareTiles);
    setTempFestiveTheme(festiveTheme);
    setTempHideFeatured(hideFeatured);
    setShowEditModal(false);
  };

  const handleRemoveBg = () => {
    setTempBgUrl('');
  };

  // Open edit modal: also prepare siteInfo temp copy
  const openEditModal = () => {
    setTempBgUrl(bgImageUrl);
    setTempSquareTiles(squareTiles);
    setTempFestiveTheme(festiveTheme);
    setTempHideFeatured(hideFeatured);
    setTempSiteInfo(siteInfo);
    setShowEditModal(true);
  };

  const handleApplyChangesExtended = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Vrei să aplici aceste modificări pe toate dispozitivele?\n\n' +
      '✓ Tema festivă\n' +
      '✓ Imaginea de fundal\n' +
      '✓ Stilul tilelor\n' +
      '✓ Informații contact\n\n' +
      'Modificările vor fi vizibile pentru toți utilizatorii după refresh.'
    );
    
    if (!confirmed) return;
    
    // existing appearance apply
    await handleApplyChanges();
    // save site info
    try { localStorage.setItem('siteInfo', JSON.stringify(tempSiteInfo)); } catch {}
    setSiteInfo(tempSiteInfo);
    
    // Save siteInfo to server
    if (token) {
      try {
        await fetch(apiUrl('/api/settings'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          },
          body: JSON.stringify({ siteInfo: tempSiteInfo })
        });
        alert('✓ Modificările au fost salvate cu succes!\n\nCelelalte dispozitive vor vedea schimbările după refresh (F5).');
      } catch (err) {
        console.error('Failed to save siteInfo to server:', err);
        alert('⚠ Modificările au fost salvate local, dar nu s-au putut sincroniza pe server.');
      }
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Festive effects overlay (ONLY snow for Christmas, bats for Halloween) */}
      <FestiveEffects theme={effectiveFestiveTheme} />
      
      {/* Background image container - desktop only, absolute position, starts after navbar */}
      {!isMobile && (showEditModal ? tempBgUrl : bgImageUrl) && (
        <div style={{
          position: 'absolute',
          top: -30, // Start at top of this container (which is below navbar)
          left: 0,
          right: 0,
          height: '535px', // Reduced to not overlap car listings
          backgroundImage: `url(${showEditModal ? tempBgUrl : bgImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 0,
          pointerEvents: 'none',
        }} />
      )}
      
      {/* Edit button - visible only for logged users on desktop */}
      {token && !isMobile && (
        <button
          onClick={openEditModal}
          style={{
            position: 'fixed',
            top: 100,
            right: 20,
            background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '12px 20px',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            boxShadow: `0 4px 16px ${tokens.indicatorGlow}`,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Edit
        </button>
      )}

      {/* Edit modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
            borderRadius: 16,
            padding: 24,
            width: 'min(500px, 90vw)',
            boxShadow: `0 20px 60px ${tokens.shadow}`,
            maxHeight: '85vh',
            overflowY: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', color: tokens.text, fontSize: 22, fontWeight: 800 }}>Edit Home Appearance</h3>
            <p style={{ margin: '0 0 16px 0', color: tokens.textMuted, fontSize: 14 }}>Change background and tile shape with live preview</p>
            
            {/* Festive Theme selector */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: tokens.text, fontWeight: 700, marginBottom: 6 }}>🎉 Festive Theme</div>
              <select
                value={tempFestiveTheme}
                onChange={(e) => setTempFestiveTheme(e.target.value)}
                style={{
                  width: '100%',
                  background: tokens.panel,
                  color: tokens.text,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                <option value="none">❌ None (Default)</option>
                <option value="christmas">🎄 Christmas (Snow & Santa Hats)</option>
                <option value="halloween">🎃 Halloween (Bats & Pumpkins)</option>
                <option value="easter">🐰 Easter (Eggs & Spring)</option>
                <option value="valentine">💖 Valentine's Day (Hearts)</option>
                <option value="newyear">🎆 New Year (Fireworks)</option>
              </select>
            </div>
            
            {/* Tile shape toggle */}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input id="squareTilesToggle" type="checkbox" checked={tempSquareTiles} onChange={(e) => setTempSquareTiles(e.target.checked)} />
              <label htmlFor="squareTilesToggle" style={{ color: tokens.text }}>Make all tiles square (no rounded corners)</label>
            </div>
            
            {/* Hide featured tile toggle */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input id="hideFeaturedToggle" type="checkbox" checked={tempHideFeatured} onChange={(e) => setTempHideFeatured(e.target.checked)} />
              <label htmlFor="hideFeaturedToggle" style={{ color: tokens.text }}>Hide "Recomandate" tile</label>
            </div>
            <div style={{ height: 1, background: tokens.border, margin: '14px 0' }} />
            <div style={{ color: tokens.text, fontWeight: 700, marginBottom: 6 }}>Hero Background Image</div>
            <p style={{ margin: '0 0 12px 0', color: tokens.textMuted, fontSize: 12 }}>Upload from PC or enter an image URL (desktop only)</p>
            
            {/* File upload section */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
              <label style={{
                display: 'inline-block',
                background: tokens.panel,
                color: tokens.text,
                border: `2px dashed ${tokens.border}`,
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 14,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  style={{ display: 'none' }}
                />
                {uploadingFile ? '⏳ Uploading...' : '📁 Choose file from PC'}
              </label>
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: tokens.border }} />
              <span style={{ color: tokens.textMuted, fontSize: 12 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: tokens.border }} />
            </div>

            {/* URL input */}
            <input
              type="text"
              value={tempBgUrl}
              onChange={(e) => setTempBgUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              style={{
                width: '100%',
                background: tokens.panel,
                color: tokens.text,
                border: `1px solid ${tokens.border}`,
                borderRadius: 8,
                padding: '12px 14px',
                fontSize: 14,
                outline: 'none',
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />
            {/* Divider for site info section */}
            <div style={{ height: 1, background: tokens.border, margin: '22px 0 12px' }} />
            <button
              onClick={() => setContactOpen(v => !v)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: tokens.panel,
                color: tokens.text,
                border: `1px solid ${tokens.border}`,
                borderRadius: 10,
                padding: '12px 14px',
                cursor: 'pointer',
                fontWeight: 800,
                letterSpacing: 0.2,
                fontSize: 14,
              }}
            >
              <span>Date contact & footer</span>
              <span style={{ transform: contactOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s ease' }}>
                ▼
              </span>
            </button>
            {contactOpen && (
              <div style={{ marginTop: 12 }}>
                <p style={{ margin: '0 0 14px', fontSize: 12, color: tokens.textMuted }}>Completează câmpurile. Vor apărea în footer și pe butoanele mobile (telefon / WhatsApp). Format recomandat WhatsApp: +407xxxxxxxx.</p>
                {[
                  ['companyName','Nume firmă'],
                  ['address','Adresă'],
                  ['hours','Program'],
                  ['phone','Telefon'],
                  ['whatsapp','WhatsApp'],
                  ['email','Email'],
                  ['privacyPolicyUrl','URL Politică confidențialitate'],
                  ['termsUrl','URL Termeni & condiții'],
                  ['anpcUrl','URL ANPC'],
                  ['cui','CUI'],
                  ['regCom','Nr. Reg. Com']
                ].map(([k,label]) => (
                  <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>{label}</label>
                    <input
                      value={tempSiteInfo[k] || ''}
                      onChange={(e) => setTempSiteInfo(prev => ({ ...prev, [k]: e.target.value }))}
                      placeholder={label}
                      style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '8px 10px', fontSize: 13 }}
                    />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 12 }}>
              <button onClick={handleRemoveBg} style={{
                background: 'transparent',
                color: '#ef4444',
                border: `1px solid #ef4444`,
                borderRadius: 8,
                padding: '10px 16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}>Remove Background</button>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={handleCancelEdit} style={{
                  background: tokens.panel,
                  color: tokens.text,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}>{tr.cancel}</button>
                <button onClick={handleApplyChangesExtended} style={{
                  background: `linear-gradient(90deg, ${tokens.accent}, ${tokens.accent2})`,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 20px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}>{tr.saveAllDevices}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile-only marquee announcement bar */}
      {isMobile && <MobileMarquee />}
      {/* Top area: on mobile show Featured above Filters via column-reverse */}
      <div style={{
        display: isMobile ? 'flex' : 'grid',
        ...(isMobile ? { flexDirection: 'column-reverse' } : { gridTemplateColumns: window.innerWidth < 980 ? '1fr' : '1.1fr 0.9fr' }),
        gap: 24,
        alignItems: 'start',
        marginBottom: 26,
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Filter Panel */}
  <div style={{
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
            borderRadius: sRadiusPanel,
            boxShadow: `0 12px 42px ${tokens.shadow}, 0 4px 12px rgba(0,0,0,0.15)`,
            overflow: 'visible',
            // Reduce left/right width on desktop and center the panel in its grid column
            width: '100%',
    // Previous width 396px -> increase by +35%
    maxWidth: isMobile ? '100%' : `${Math.round(396 * boostFactor)}px`,
            margin: isMobile ? undefined : '0 auto',
            justifySelf: isMobile ? undefined : 'center',
          }}>
           <div style={{ padding: sInnerPad }}>
            {/* Compact set on mobile, full set otherwise or when advanced toggled */}
            {(!isMobile || showAdvanced) ? (
              <div style={grid}>
                {/* Brand */}
                <div>
                  <div style={fieldLabel}>{tr.brand}</div>
                  <FancyDropdown
                    options={brands}
                    value={filters.brand}
                    onChange={(val) => setFilters(prev => ({ ...prev, brand: val, model: '' }))}
                    placeholder={tr.choose}
                  />
                </div>
                {/* Model select (disabled until brand) */}
                <div>
                  <div style={fieldLabel}>{tr.model}</div>
                  {filters.brand ? (
                    <FancyDropdown
                      options={models}
                      value={filters.model}
                      onChange={(val) => setFilters(prev => ({ ...prev, model: val }))}
                      placeholder={tr.choose}
                    />
                  ) : (
                    <div style={{ opacity: 0.6 }}>
                      <FancyDropdown
                        options={[]}
                        value={''}
                        onChange={() => {}}
                        placeholder={tr.choose}
                        disabled
                      />
                    </div>
                  )}
                </div>
                {/* Year */}
                <div>
                  <div style={fieldLabel}>{tr.year}</div>
                  <div style={{ display: 'flex', gap: sGapSmall }}>
                    <div style={{ flex: 1 }}>
                      <FancyDropdown
                        options={years}
                        value={filters.yearFrom}
                        onChange={(val) => setFilters(prev => ({ ...prev, yearFrom: val }))}
                        placeholder={tr.yearFrom}
                        toLowerValue={false}
                        allowSearch={false}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <FancyDropdown
                        options={years}
                        value={filters.yearTo}
                        onChange={(val) => setFilters(prev => ({ ...prev, yearTo: val }))}
                        placeholder={tr.yearTo}
                        toLowerValue={false}
                        allowSearch={false}
                      />
                    </div>
                  </div>
                </div>
                {/* KM */}
                <div>
                  <div style={fieldLabel}>{tr.km}</div>
                  <div style={{ display: 'flex', gap: sGapSmall }}>
                    <input name="kmFrom" value={filters.kmFrom} onChange={handleChange} placeholder={tr.kmFrom} style={{ ...inputBase, flex: 1 }} />
                    <input name="kmTo" value={filters.kmTo} onChange={handleChange} placeholder={tr.kmTo} style={{ ...inputBase, flex: 1 }} />
                  </div>
                </div>
                {/* Price */}
                <div>
                  <div style={fieldLabel}>{tr.price}</div>
                  <div style={{ display: 'flex', gap: sGapSmall }}>
                    <div style={{ flex: 1 }}>
                      <FancyDropdown
                        options={priceOptions.map(p => `${formatNumberSpaces(p)} €`)}
                        value={filters.priceFrom ? `${formatNumberSpaces(filters.priceFrom)} €` : ''}
                        onChange={(val) => {
                          const num = val.replace(/[^\d]/g, '');
                          setFilters(prev => ({ ...prev, priceFrom: num }));
                        }}
                        placeholder={tr.priceFrom}
                        toLowerValue={false}
                        allowSearch={false}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <FancyDropdown
                        options={priceOptions.map(p => `${formatNumberSpaces(p)} €`)}
                        value={filters.priceTo ? `${formatNumberSpaces(filters.priceTo)} €` : ''}
                        onChange={(val) => {
                          const num = val.replace(/[^\d]/g, '');
                          setFilters(prev => ({ ...prev, priceTo: num }));
                        }}
                        placeholder={tr.priceTo}
                        toLowerValue={false}
                        allowSearch={false}
                      />
                    </div>
                  </div>
                </div>
                {/* Fuel */}
                <div>
                  <div style={fieldLabel}>{tr.fuel}</div>
                  <FancyDropdown
                    options={fuels}
                    value={filters.fuel}
                    onChange={(val) => setFilters(prev => ({ ...prev, fuel: val }))}
                    placeholder={tr.choose}
                  />
                </div>
                {/* Transmission */}
                <div>
                  <div style={fieldLabel}>{tr.transmission}</div>
                  <FancyDropdown
                    options={transmissions}
                    value={filters.transmission}
                    onChange={(val) => setFilters(prev => ({ ...prev, transmission: val }))}
                    placeholder={tr.choose}
                  />
                </div>
                {/* Body */}
                <div>
                  <div style={fieldLabel}>{tr.body}</div>
                  <FancyDropdown
                    options={bodies}
                    value={filters.body}
                    onChange={(val) => setFilters(prev => ({ ...prev, body: val }))}
                    placeholder={tr.choose}
                  />
                </div>
              </div>
            ) : (
              <div style={compactGrid}>
                {/* Brand */}
                <div>
                  <div style={fieldLabel}>{tr.brand}</div>
                  <FancyDropdown
                    options={brands}
                    value={filters.brand}
                    onChange={(val) => setFilters(prev => ({ ...prev, brand: val, model: '' }))}
                    placeholder={tr.choose}
                  />
                </div>
                {/* Model disabled until brand */}
                <div>
                  <div style={fieldLabel}>{tr.model}</div>
                  {filters.brand ? (
                    <FancyDropdown
                      options={models}
                      value={filters.model}
                      onChange={(val) => setFilters(prev => ({ ...prev, model: val }))}
                      placeholder={tr.choose}
                    />
                  ) : (
                    <div style={{ opacity: 0.6 }}>
                      <FancyDropdown
                        options={[]}
                        value={''}
                        onChange={() => {}}
                        placeholder={tr.choose}
                        disabled
                      />
                    </div>
                  )}
                </div>
                {/* Price to (dropdown on mobile compact) */}
                <div>
                  <div style={fieldLabel}>{tr.price}</div>
                  <FancyDropdown
                    options={priceOptions.map(p => `${formatNumberSpaces(p)} €`)}
                    value={filters.priceTo ? `${formatNumberSpaces(filters.priceTo)} €` : ''}
                    onChange={(val) => {
                      const num = val.replace(/[^\d]/g, '');
                      setFilters(prev => ({ ...prev, priceTo: num }));
                    }}
                    placeholder={tr.priceTo}
                    toLowerValue={false}
                    allowSearch={false}
                  />
                </div>
                {/* Year from */}
                <div>
                  <div style={fieldLabel}>{tr.year}</div>
                  <FancyDropdown
                    options={years}
                    value={filters.yearFrom}
                    onChange={(val) => setFilters(prev => ({ ...prev, yearFrom: val }))}
                    placeholder={tr.yearFrom}
                    toLowerValue={false}
                    allowSearch={false}
                  />
                </div>
                {/* Body */}
                <div>
                  <div style={fieldLabel}>{tr.body}</div>
                  <FancyDropdown
                    options={bodies}
                    value={filters.body}
                    onChange={(val) => setFilters(prev => ({ ...prev, body: val }))}
                    placeholder={tr.choose}
                  />
                </div>
                {/* Fuel */}
                <div>
                  <div style={fieldLabel}>{tr.fuel}</div>
                  <FancyDropdown
                    options={fuels}
                    value={filters.fuel}
                    onChange={(val) => setFilters(prev => ({ ...prev, fuel: val }))}
                    placeholder={tr.choose}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: sGap, marginTop: sGap, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }} />
              <button onClick={applyAndScroll} style={{ background: `linear-gradient(90deg, ${tokens.accent}, ${tokens.accent2})`, color: '#ffffff', border: 'none', borderRadius: btnRadius, padding: `${btnPadV}px ${btnPadH}px`, fontWeight: 900, fontSize: btnFont, boxShadow: btnShadow }}>
                {tr.see}
              </button>
            </div>

            {/* Mobile toggle to advanced placed under the See button */}
            {isMobile && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                <button onClick={() => setShowAdvanced(s => !s)} style={{ background: 'none', border: 'none', color: tokens.accent, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 7h16M7 12h10M9 17h6" stroke={tokens.accent} strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  {tr.searchDetailed}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Featured panel (slideshow) - conditionally hidden */}
        {!effectiveHideFeatured && (
        <div style={{
          background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`,
          borderRadius: featuredOuterRadius,
          padding: 2,
          width: window.innerWidth < 760 ? '320px' : '100%',
          maxWidth: window.innerWidth < 760 ? '96vw' : `${Math.round(396 * boostFactor)}px`,
          margin: window.innerWidth < 760 ? '32px auto 18px auto' : 0,
          justifySelf: window.innerWidth < 760 ? undefined : 'start',
          boxShadow: `0 14px 44px ${tokens.indicatorGlow}, 0 4px 12px rgba(0,0,0,0.15)`,
          position: 'relative',
        }}>
          {/* Festive hat decoration on colored gradient corner - ONLY Christmas */}
          {effectiveFestiveTheme === 'christmas' && (
            <SantaHat accentColor={tokens.accent} isFeatured={true} />
          )}
          
          {/* Header sits directly on gradient so corners are continuous */}
          <div style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: window.innerWidth < 760 ? 44 : 46,
          }}>
            <div style={{
              color: isLightTheme ? '#ffffff' : tokens.text,
              fontWeight: isLightTheme ? 800 : 900,
              textTransform: 'uppercase',
              fontFamily: isLightTheme 
                ? '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                : 'Montserrat, Inter, Nunito, Segoe UI, Arial, Helvetica, sans-serif',
              fontSize: window.innerWidth < 760 ? 18 : 20,
              letterSpacing: isLightTheme ? '1.8px' : '1.5px',
              textShadow: isLightTheme
                ? '0 2px 12px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.5)'
                : `0 2px 8px ${tokens.indicatorGlow}, 0 1px 0 ${tokens.panel}`,
              textAlign: 'center',
            }}>
              {tr.featured.toUpperCase()}
            </div>
          </div>
          {/* Dark content container below header */}
          <div style={{
            background: tokens.surface,
            borderRadius: featuredInnerRadius,
            boxShadow: `0 8px 32px ${tokens.shadow}`,
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Snow accumulation only (no hat here) - ONLY Christmas */}
            {effectiveFestiveTheme === 'christmas' && (
              <SnowAccumulation isFeatured={true} isDark={tokens.isDark} />
            )}
            {/* Halloween decorations */}
            {effectiveFestiveTheme === 'halloween' && getTileDecoration(effectiveFestiveTheme, tokens.accent, themeKey, true, tokens.isDark)}
            
            <div style={{ position: 'relative' }}>
            {featured && featured.length ? (
              <div style={{ padding: 12 }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
                <div onClick={() => navigate(`/car/${featured[featuredIdx]._id}`)} style={{ cursor: 'pointer' }}>
                  {featured[featuredIdx].image && (
                    <img
                      src={imageUrl(featured[featuredIdx].image)}
                      alt={featured[featuredIdx].title || featured[featuredIdx].model}
                      style={{
                        width: '100%',
                        height: window.innerWidth < 760 ? 140 : 260,
                        objectFit: 'cover',
                        borderRadius: imageRadius,
                        boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
                        display: 'block',
                        marginBottom: window.innerWidth < 760 ? 8 : 16,
                      }}
                    />
                  )}
                  {(featured[featuredIdx].status === 'sold' || featured[featuredIdx].sold) && (
                    <div style={{
                      position: 'absolute',
                      top: 16,
                      left: -46,
                      transform: 'rotate(-45deg)',
                      background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                      color: '#fff',
                      fontWeight: 900,
                      width: 150,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      boxShadow: '0 4px 10px #0006',
                    }}>{tr.sold}</div>
                  )}
                  {featured[featuredIdx].status === 'reserved' && (
                    <div style={{
                      position: 'absolute',
                      top: 16,
                      left: -46,
                      transform: 'rotate(-45deg)',
                      background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                      color: '#1b1b1b',
                      fontWeight: 900,
                      width: 150,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      boxShadow: '0 4px 10px #0006',
                    }}>{tr.reserved}</div>
                  )}
                  <div style={{ padding: '12px 4px' }}>
                    <div style={{ fontSize: window.innerWidth < 760 ? 23 : 26, fontWeight: 700, color: tokens.text, lineHeight: 1.3, marginBottom: 6 }}>
                      {(featured[featuredIdx].marca || (featured[featuredIdx].title?.split(' ')[0]))} {featured[featuredIdx].model}
                    </div>
                    <div style={{ color: tokens.accent, fontWeight: 800, fontSize: window.innerWidth < 760 ? 21 : 24 }}>{formatNumberSpaces(featured[featuredIdx].price)} €</div>
                  </div>
                </div>
                {/* Navigation arrows removed as requested */}
                {featured.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 6, paddingBottom: 6 }}>
                    {featured.slice(0, 6).map((_, i) => (
                      <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i === featuredIdx ? tokens.accent : tokens.border }} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              initialized ? (
                <div style={{ padding: 24, color: tokens.textMuted }}>Nu există anunțuri</div>
              ) : (
                <div style={{ padding: 24, color: tokens.textMuted }}> </div>
              )
            )}
            </div>
          </div>
        </div>
        )}

      </div>

    {/* Title */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
  <h2 style={{ fontSize: 32, fontWeight: 700, letterSpacing: 1, color: tokens.text, margin: 0 }}>{tr.title}</h2>
      </div>

      {/* Results grid */}
      <div
        ref={resultsRef}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 32,
          justifyContent: 'center',
        }}
      >
        {filtered.map(car => (
          <div
            key={car._id}
            style={{
              background: tokens.surface,
              borderRadius: tileRadius,
              boxShadow: `0 4px 24px ${tokens.shadow}`,
              padding: 24,
              width: '320px',
              maxWidth: '96vw',
              color: tokens.text,
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'pointer',
              border: `1px solid ${tokens.border}`,
              position: 'relative',
              overflow: 'visible',
              fontFamily: 'Montserrat, Inter, Nunito, Segoe UI, Arial, Helvetica, sans-serif',
              marginBottom: 18,
              boxSizing: 'border-box',
            }}
            onMouseEnter={e => {
              if (window.innerWidth > 600) e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={e => {
              if (window.innerWidth > 600) e.currentTarget.style.transform = 'scale(1)';
            }}
            onClick={() => navigate(`/car/${car._id}`)}
          >
            {/* Festive decoration - ONLY Christmas and Halloween */}
            {getTileDecoration(effectiveFestiveTheme, tokens.accent, themeKey, false, tokens.isDark)}
            
            {/* Inner wrapper for content overflow control */}
            <div style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: tileRadius,
            }}>
              {car.image && (
                <img
                  src={imageUrl(car.image)}
                  alt={car.title}
                  style={{
                    width: '100%',
                    height: window.innerWidth < 600 ? 120 : 160,
                    objectFit: 'cover',
                    borderRadius: imageRadius,
                    marginBottom: 16,
                    boxShadow: `0 2px 8px ${tokens.shadowSoft}`,
                    display: 'block',
                  }}
                />
              )}
              {(car.status === 'sold' || car.sold) && (
                <div style={{
                  position: 'absolute',
                  top: 16,
                  left: -46,
                  transform: 'rotate(-45deg)',
                  background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                  color: '#fff',
                  fontWeight: 900,
                  width: 150,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 10px #0006',
                }}>{tr.sold}</div>
              )}
              {car.status === 'reserved' && (
                <div style={{
                  position: 'absolute',
                  top: 16,
                  left: -46,
                  transform: 'rotate(-45deg)',
                  background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                  color: '#1b1b1b',
                  fontWeight: 900,
                  width: 150,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 10px #0006',
                }}>{tr.reserved}</div>
              )}
            </div>
            <h3
              style={{
                fontSize: window.innerWidth < 600 ? 18 : 24,
                fontWeight: 600,
                margin: '8px 0 4px',
                wordBreak: 'break-word',
              }}
            >
              {car.marca || (car.title?.split(' ')[0])} {car.model}
            </h3>
            <p style={{ margin: '4px 0', fontSize: window.innerWidth < 600 ? 16 : 20, color: tokens.accent, fontWeight: 700 }}>{formatNumberSpaces(car.price)} €</p>
            <p style={{ margin: '4px 0', fontSize: window.innerWidth < 600 ? 13 : 16 }}><b>{tr.type}:</b> {car.type}</p>
            <p style={{ margin: '4px 0', fontSize: window.innerWidth < 600 ? 13 : 16 }}><b>{tr.year}:</b> {car.year}</p>
            <p style={{ margin: '4px 0', fontSize: window.innerWidth < 600 ? 13 : 16 }}><b>{tr.model}:</b> {car.model}</p>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 40, height: 40, background: tokens.accent, borderRadius: effectiveSquareTiles ? '0' : '0 16px 0 16px', clipPath: 'polygon(100% 0, 100% 100%, 0 0)' }}></div>
          </div>
        ))}
      </div>
    </div>
  );
}
